
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookOpen, ShoppingCart, Users, DollarSign, Package, Truck, AlertTriangle } from "lucide-react";
import { Order, Book, Customer, Dashboard as DashboardApi } from '@/api/entities';
import { useNotifications } from '@/hooks/useNotifications';
import { useLocalization } from '../components/providers/LocalizationProvider';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { showSuccess, showError } = useNotifications();
  const { currencySymbol } = useLocalization();
  
  const [stats, setStats] = useState({
    revenue: { ytd: 0, mtd: 0, today: 0 },
    customers: { total: 0, ytd_new: 0, mtd_new: 0, today_new: 0 },
    orders: { shipped: { ytd: 0, mtd: 0, today: 0 }, not_shipped_statuses: { ordered: 0, paid: 0, signed: 0, packed: 0 } },
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [deliveryStats, setDeliveryStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      // Fetch complete dashboard data including delivery stats
      const dashboardData = await DashboardApi.getDashboardData();
      
      setStats(dashboardData.kpis);
      setDeliveryStats(dashboardData.deliveryStats);

      // Get recent orders
      const recent = dashboardData.recentOrders
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      setRecentOrders(recent);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showError('errors.network.connection_failed');
    } finally {
      setIsLoading(false);
    }
  };



  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('pages.dashboard.title')}</h1>
          <p className="text-slate-600 mt-1">{t('pages.dashboard.subtitle')}</p>
        </div>
        

      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('pages.dashboard.revenue_ytd')}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currencySymbol}{Number(stats.revenue?.ytd || 0).toFixed(2)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('pages.dashboard.revenue_mtd')}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currencySymbol}{Number(stats.revenue?.mtd || 0).toFixed(2)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('pages.dashboard.revenue_today')}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currencySymbol}{Number(stats.revenue?.today || 0).toFixed(2)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('pages.dashboard.customers_total')}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.customers?.total || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('pages.dashboard.customers_growth')} YTD {stats.customers?.ytd_new || 0} • MTD {stats.customers?.mtd_new || 0} • {t('general.today')} {stats.customers?.today_new || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Orders summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('pages.dashboard.orders_shipped')}</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              YTD: <span className="font-semibold text-slate-900">{stats.orders?.shipped?.ytd || 0}</span>
              <span className="mx-2">|</span>
              MTD: <span className="font-semibold text-slate-900">{stats.orders?.shipped?.mtd || 0}</span>
              <span className="mx-2">|</span>
              {t('general.today')}: <span className="font-semibold text-slate-900">{stats.orders?.shipped?.today || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('pages.dashboard.orders_not_shipped')}</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center justify-between"><span>Ordered</span><span className="font-semibold">{stats.orders?.not_shipped_statuses?.ordered || 0}</span></div>
              <div className="flex items-center justify-between"><span>Paid</span><span className="font-semibold">{stats.orders?.not_shipped_statuses?.paid || 0}</span></div>
              <div className="flex items-center justify-between"><span>Signed</span><span className="font-semibold">{stats.orders?.not_shipped_statuses?.signed || 0}</span></div>
              <div className="flex items-center justify-between"><span>Packed</span><span className="font-semibold">{stats.orders?.not_shipped_statuses?.packed || 0}</span></div>
            </div>
            
            {/* Delivery Issues Section */}
            {(deliveryStats?.metaship_order_is_pending > 0 || deliveryStats?.metaship_order_is_failed > 0) && (
              <div className="mt-4 pt-3 border-t border-slate-200">
                <div className="text-xs font-medium text-slate-600 mb-2">{t('pages.dashboard.delivery_issues')}</div>
                <div className="space-y-1 text-sm">
                  {deliveryStats?.metaship_order_is_pending > 0 && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-yellow-600" />
                        <span className="text-slate-700">{t('delivery_status.metaship_order_is_pending')}</span>
                      </div>
                      <span className="font-semibold text-yellow-600">{deliveryStats.metaship_order_is_pending}</span>
                    </div>
                  )}
                  {deliveryStats?.metaship_order_is_failed > 0 && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-red-600" />
                        <span className="text-slate-700">{t('delivery_status.metaship_order_is_failed')}</span>
                      </div>
                      <span className="font-semibold text-red-600">{deliveryStats.metaship_order_is_failed}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>


      {/* Recent Orders */}
      <Card>
        <CardHeader>
          <CardTitle>{t('pages.dashboard.recent_orders')}</CardTitle>
        </CardHeader>
        <CardContent>
          {recentOrders.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">{t('pages.dashboard.no_recent_orders')}</p>
              <p className="text-sm text-muted-foreground">{t('pages.dashboard.no_recent_orders_desc')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recentOrders.map((order) => (
                <Link key={order.id} to={`/panel/orders?order_id=${order.id}`} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                  <div>
                    <p className="font-medium">{t('order.order_number', { number: order.order_number || order.id })}</p>
                    <p className="text-sm text-muted-foreground">
                      {order.customer?.full_name || order.customer_name || t('customer.name')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{currencySymbol}{Number(order.total_amount || 0).toFixed(2)}</p>
                    <Badge variant="outline">{t(`public_order.status.${order.status}`)}</Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>


    </div>
  );
}
